import { useState } from 'react';

import {
  Badge,
  cardStyle,
  IdChip,
  isBlocked,
  rankIssuesBySeverity,
  realBlockers,
  Section,
  splitCatalogEntry,
  text,
} from './optimizationBreakdownPrimitives';

/**
 * MMGY DV360 optimization breakdown.
 *
 * Same three-part shape as the Crimtan breakdown — cause, then the existing suggestion
 * cards, then expansions — because both are rendering the same combined-optimizer result
 * (campaign_analysis, delivery_diagnosis, audience_expansion_recommendations). What is
 * MMGY-specific is how that result READS, and it is specific enough that the Crimtan view
 * actively misleads on an MMGY campaign:
 *
 *   THE RETARGETING DONUT. An MMGY Display or Native retargeting placement is expanded at
 *   build time into SEVEN line items — one per recency window, differing only by a suffix
 *   ("0d - 1d" … "120d - all"), each with its own bid and caps. Rendered one card per line
 *   item, a single stalled retargeting tactic fills the Cause section with seven
 *   near-identical rows and buries everything else. They are one finding, so they render as
 *   one card with the windows inside it.
 *
 *   NAMES THAT ARE DATA. MMGY IO names are twelve underscore-separated components
 *   (FY26_spring_Leisure_..._Display_Prospecting_dv360_US_NA_season_<advertiser>_AV) and
 *   line item names are six. Printed raw they are 200-character strings that wrap over
 *   three lines and read as noise; parsed, they are the channel, the tactic and the
 *   geography — the things you need to know to judge the row.
 *
 *   ADDED VALUE. An IO whose last name component is AV is bonus inventory with no media
 *   cost behind it. That has to be visible on the row, because every cost-efficiency
 *   instinct a reader brings to the screen is wrong for those lines.
 *
 * The suggestion cards in the middle are deliberately left alone: they own the
 * apply/applied state keyed on the backend's original suggestion index, so wrapping rather
 * than replacing them keeps Apply working exactly as it does on every other platform.
 */

export const MMGY_CLIENT = 'mmgy';

/**
 * True when an optimizer result was steered by the MMGY client prompt.
 *
 * Keys off `client_name`, which the launcher echoes onto the result with the slug that
 * actually resolved. The campaign detail screen never receives the account name, so the
 * result itself is the only place this is knowable — and a slug that resolved is also proof
 * the client prompt really ran, which an account-name check on its own would not give us.
 *
 * Exact match on the slug, like the Crimtan check: app-backend resolves 'mmgy-prod' to
 * 'mmgy' before it is echoed, so a sub-account name arriving here is not a resolved client
 * and must not get the client layout.
 */
export function isMmgyOptimizerResult(jsonResponse) {
  const client = jsonResponse?.client_name;
  return typeof client === 'string' && client.toLowerCase() === MMGY_CLIENT;
}

/* ------------------------------------------------------------------ */
/* MMGY naming                                                         */
/* ------------------------------------------------------------------ */

/**
 * The recency-window suffixes the launcher appends when it expands one retargeting
 * placement into the donut (RT_RECENCY_WINDOWS in update_complete_dv360_final.py). Ordered
 * as the pool ages, which is the order they have to render in: the windows are fed in
 * sequence, so "0d - 1d" at zero while the rest deliver means something different from the
 * reverse, and a reader can only see that if the rows are in age order.
 */
export const RECENCY_WINDOWS = [
  '0d - 1d', '1d - 7d', '7d - 14d', '14d - 30d', '30d - 90d', '90d - 120d', '120d - all',
];

// Anchored to the END of the name and tolerant of internal whitespace, because the suffix
// is appended to a base name that itself contains " - " separators.
const WINDOW_SUFFIX_RE = new RegExp(
  `\\s+(${RECENCY_WINDOWS.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s*')).join('|')})\\s*$`,
  'i',
);

/**
 * Split a line item name into its donut base and recency window.
 *
 * Returns `{ base, window }` with `window` null for anything that is not a donut line —
 * every prospecting line, every lookalike line, and the single flat retargeting line that
 * Video / YouTube / CTV / Audio retargeting produces instead of a donut.
 */
export function splitRecencyWindow(name) {
  const raw = text(name);
  if (!raw) return { base: null, window: null };
  const match = raw.match(WINDOW_SUFFIX_RE);
  if (!match) return { base: raw, window: null };
  const base = raw.slice(0, match.index).trim();
  // A name that is ONLY a window suffix has no base to group siblings by, so it is left
  // ungrouped rather than collapsed into a card labelled with nothing.
  if (!base) return { base: raw, window: null };
  return { base, window: match[1].replace(/\s+/g, ' ').trim().toLowerCase() };
}

/** Where a window sits in the ageing order; unknown windows sort last, stably. */
function windowRank(window) {
  const i = RECENCY_WINDOWS.findIndex((w) => w.toLowerCase() === String(window).toLowerCase());
  return i === -1 ? RECENCY_WINDOWS.length : i;
}

/**
 * The readable facts inside an MMGY name.
 *
 * IO names are twelve underscore components in a fixed order; line item names are six in a
 * different fixed order. Both are parsed positionally, which is only safe because the
 * launcher builds them programmatically with a guaranteed component count (_assemble_io_name
 * pads to exactly twelve). A name that does not have the expected shape — an older IO
 * trafficked before the convention, or a hand-edited one — returns nulls and the caller
 * falls back to printing it verbatim. Guessing at a name that does not fit is worse than
 * showing the raw string.
 */
export function parseIoName(name) {
  const raw = text(name);
  if (!raw) return null;
  const parts = raw.split('_').map((p) => p.trim());
  if (parts.length !== 12) return null;
  const [fiscalYear, timePeriod, initiative, initiativeDetail, channel, targeting,
    channelDetail, country, geography, timePeriodType, advertiser, paidAv] = parts;
  return {
    fiscalYear, timePeriod, initiative, initiativeDetail, channel, targeting,
    channelDetail, country, geography, timePeriodType, advertiser,
    isAddedValue: String(paidAv).toLowerCase() === 'av',
  };
}

/** Channel / inventory / audience-category out of a line item name, when it carries them. */
export function parseLineItemName(name) {
  const raw = text(name);
  if (!raw) return null;
  const parts = raw.split('_').map((p) => p.trim());
  if (parts.length < 3) return null;
  const [channel, inventory, audienceCategory] = parts;
  if (!channel || !inventory) return null;
  return {
    channel,
    inventory,
    audienceCategory: audienceCategory || null,
    // Everything after the fixed head is the descriptor — the part that actually tells two
    // sibling lines apart, and the only part worth showing at full width.
    descriptor: parts.slice(3).filter(Boolean).join(' · ') || null,
  };
}

/**
 * An MMGY name rendered as chips rather than a 200-character underscore string.
 *
 * Falls back to the raw name whenever parsing did not produce the expected shape, so an
 * unconventional name is still fully readable — just unstyled.
 */
export const NameChips = ({ name, parsed }) => {
  const raw = text(name);
  if (!parsed) {
    return <strong style={{ fontSize: '0.9em', wordBreak: 'break-word' }}>{raw}</strong>;
  }
  const facets = [parsed.channel, parsed.targeting || parsed.inventory,
    parsed.audienceCategory, parsed.geography].filter(Boolean);
  const headline = parsed.descriptor || parsed.initiativeDetail || raw;
  return (
    <>
      <strong style={{ fontSize: '0.9em', wordBreak: 'break-word' }}>{headline}</strong>
      <span style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
        {facets.map((f, i) => (
          <span key={`${f}-${i}`} style={{
            background: '#f3f4f6', color: '#4b5563', padding: '1px 6px', borderRadius: '3px',
            fontSize: '0.75em', fontWeight: 600,
          }}>{f}</span>
        ))}
      </span>
    </>
  );
};

/* ------------------------------------------------------------------ */
/* 1. CAUSE                                                            */
/* ------------------------------------------------------------------ */

/**
 * Group diagnosis entries so the seven recency windows of one retargeting placement read
 * as one finding.
 *
 * A group is only formed where a base name has MORE THAN ONE window: a single donut line
 * surviving on its own (the others paused, or a Video retargeting line that never had
 * siblings) is not a donut and gets an ordinary row. Non-donut entries keep their own
 * identity and their first-seen order, so grouping never reorders the list around them.
 */
export function groupRecencyWindows(entries) {
  const out = [];
  const byBase = new Map();

  for (const entry of (Array.isArray(entries) ? entries : [])) {
    if (!entry || typeof entry !== 'object') continue;
    const { base, window } = splitRecencyWindow(entry.line_item_name);
    if (!window) {
      out.push({ kind: 'single', entry });
      continue;
    }
    if (!byBase.has(base)) {
      const group = { kind: 'donut', base, windows: [] };
      byBase.set(base, group);
      out.push(group);
    }
    byBase.get(base).windows.push({ entry, window });
  }

  // A "donut" of one is just a line item, so it is demoted rather than rendered as a group
  // with a single row and a summary that says "1 of 1 windows blocked".
  return out.map((item) => {
    if (item.kind !== 'donut') return item;
    if (item.windows.length < 2) return { kind: 'single', entry: item.windows[0].entry };
    item.windows.sort((a, b) => windowRank(a.window) - windowRank(b.window));
    return item;
  });
}

/**
 * Why the campaign is not delivering: the pacing headline, the ranked issues, and the
 * per-line-item delivery diagnosis with the retargeting donut collapsed into one card.
 *
 * Blocked line items are listed in full and unconditionally — a line item that cannot serve
 * makes every recommendation below it moot, so it must not be behind a disclosure. The line
 * items that CAN deliver are collapsed by default: on a real MMGY campaign, which routinely
 * carries several channels' worth of prospecting, they are most of the list and carry no
 * action.
 */
export function MmgyCauseSection({ details }) {
  const [showHealthy, setShowHealthy] = useState(false);

  const ca = (details?.campaign_analysis && typeof details.campaign_analysis === 'object')
    ? details.campaign_analysis
    : {};
  const keyIssues = Array.isArray(ca.key_issues) ? ca.key_issues : [];
  const diagnosis = (Array.isArray(details?.delivery_diagnosis) ? details.delivery_diagnosis : [])
    .filter(Boolean);

  const pacingSummary = text(ca.pacing_summary);
  const overallPerformance = text(ca.overall_performance);

  if (!pacingSummary && !overallPerformance && !keyIssues.length && !diagnosis.length) {
    return null;
  }

  const rankedIssues = rankIssuesBySeverity(keyIssues);

  const grouped = groupRecencyWindows(diagnosis);
  // A donut counts as blocked when ANY of its windows is: the windows share one audience and
  // one creative assignment, so a blocker on one is almost always a blocker on the tactic.
  // Erring the other way would hide it behind the collapsed disclosure.
  const isGroupBlocked = (item) => (item.kind === 'donut'
    ? item.windows.some((w) => isBlocked(w.entry))
    : isBlocked(item.entry));
  const blocked = grouped.filter(isGroupBlocked);
  const healthy = grouped.filter((item) => !isGroupBlocked(item));

  const renderBlockerBadges = (entry) => {
    const blockers = realBlockers(entry.blockers);
    if (!blockers.length) return null;
    return (
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
        {blockers.map((b, bi) => <Badge key={bi} level="high">{b.replace(/_/g, ' ')}</Badge>)}
      </div>
    );
  };

  const renderSingle = (entry, key) => {
    const lineItemId = text(entry.line_item_id);
    const evidence = text(entry.evidence);
    const blockedRow = isBlocked(entry);
    const parsed = parseLineItemName(entry.line_item_name);
    return (
      <div key={key} style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
          <NameChips name={text(entry.line_item_name) || lineItemId} parsed={parsed} />
          {lineItemId && <IdChip>{lineItemId}</IdChip>}
          <Badge level={blockedRow ? 'blocked' : 'ok'}>
            {blockedRow ? 'cannot deliver' : 'can deliver'}
          </Badge>
        </div>
        {renderBlockerBadges(entry)}
        {evidence && <p style={{ margin: 0, fontSize: '0.85em', color: '#444' }}>{evidence}</p>}
      </div>
    );
  };

  const renderDonut = (group, key) => {
    const blockedWindows = group.windows.filter((w) => isBlocked(w.entry));
    const allBlocked = blockedWindows.length === group.windows.length;
    const parsed = parseLineItemName(group.base);
    return (
      <div key={key} style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
          <NameChips name={group.base} parsed={parsed} />
          <Badge level="underpacing">retargeting donut</Badge>
          <Badge level={blockedWindows.length ? 'blocked' : 'ok'}>
            {allBlocked
              ? `all ${group.windows.length} windows blocked`
              : `${blockedWindows.length} of ${group.windows.length} windows blocked`}
          </Badge>
        </div>
        <p style={{ margin: '0 0 8px', fontSize: '0.78em', color: '#6b7280' }}>
          {allBlocked
            ? 'Every recency window is blocked — this is one tactic failing, not seven line items. Check the audience and the creative assignment on the placement.'
            : 'The windows are fed in sequence as users age through them, so the newest window is the leading indicator for the rest.'}
        </p>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82em' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: '#6b7280' }}>
              <th style={{ padding: '2px 6px 4px 0', fontWeight: 600 }}>Window</th>
              <th style={{ padding: '2px 6px 4px 0', fontWeight: 600 }}>Status</th>
              <th style={{ padding: '2px 0 4px', fontWeight: 600 }}>Evidence</th>
            </tr>
          </thead>
          <tbody>
            {group.windows.map(({ entry, window }, i) => {
              const blockers = realBlockers(entry.blockers);
              return (
                <tr key={`${window}-${i}`} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '5px 6px 5px 0', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                    {window}
                  </td>
                  <td style={{ padding: '5px 6px 5px 0' }}>
                    <Badge level={isBlocked(entry) ? 'blocked' : 'ok'}>
                      {blockers.length ? blockers[0].replace(/_/g, ' ') : (isBlocked(entry) ? 'blocked' : 'ok')}
                    </Badge>
                  </td>
                  <td style={{ padding: '5px 0', color: '#444' }}>{text(entry.evidence)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderItem = (item, i) => (item.kind === 'donut'
    ? renderDonut(item, `donut-${item.base}-${i}`)
    // Index is part of the key because two diagnosis entries can name the same line item.
    : renderSingle(item.entry, `${text(item.entry.line_item_id) || 'li'}-${i}`));

  const healthyCount = healthy.reduce(
    (n, item) => n + (item.kind === 'donut' ? item.windows.length : 1), 0);

  return (
    <Section
      step="1"
      title="Cause"
      subtitle="What is holding delivery back, before any change is recommended."
    >
      {pacingSummary && (
        <div style={{
          padding: '12px 15px', background: '#fffbeb', border: '1px solid #fde68a',
          borderRadius: '6px', marginBottom: '8px', fontSize: '0.88em', lineHeight: 1.5,
        }}>
          <strong style={{ display: 'block', marginBottom: '4px' }}>Pacing</strong>
          {pacingSummary}
        </div>
      )}

      {overallPerformance && (
        <div style={{
          padding: '12px 15px', background: '#f8f9ff', borderRadius: '6px',
          marginBottom: '8px', fontSize: '0.88em', lineHeight: 1.5,
        }}>
          {overallPerformance}
        </div>
      )}

      {rankedIssues.length > 0 && (
        <div style={{ marginTop: '10px' }}>
          <h6 style={{ margin: '0 0 2px', fontSize: '0.85em', color: '#374151' }}>Key issues</h6>
          {rankedIssues.map((issue, i) => {
            // An entry may be a bare string rather than an object.
            const issueText = text(typeof issue === 'object' && issue !== null ? issue.issue : issue);
            const severity = text(issue?.severity);
            const affected = (Array.isArray(issue?.affected_line_items) ? issue.affected_line_items : [])
              .map(text).filter(Boolean);
            return (
              <div key={i} style={cardStyle}>
                {severity && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                    <Badge level={severity}>{severity}</Badge>
                  </div>
                )}
                {issueText && <p style={{ margin: 0, fontSize: '0.88em' }}>{issueText}</p>}
                {affected.length > 0 && (
                  <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginTop: '6px' }}>
                    {affected.map((li, li_i) => <IdChip key={`${li}-${li_i}`}>{li}</IdChip>)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {blocked.length > 0 && (
        <div style={{ marginTop: '10px' }}>
          <h6 style={{ margin: '0 0 2px', fontSize: '0.85em', color: '#b91c1c' }}>
            Cannot deliver ({blocked.length})
          </h6>
          {blocked.map(renderItem)}
        </div>
      )}

      {healthy.length > 0 && (
        <div style={{ marginTop: '10px' }}>
          <button
            type="button"
            aria-expanded={showHealthy}
            onClick={() => setShowHealthy((v) => !v)}
            style={{
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              color: '#0F7B5F', fontSize: '0.85em', fontWeight: 600,
            }}
          >
            {showHealthy ? '▾' : '▸'} {healthyCount} line item{healthyCount === 1 ? '' : 's'} can deliver
          </button>
          {showHealthy && healthy.map(renderItem)}
        </div>
      )}
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* 3. EXPANSIONS                                                       */
/* ------------------------------------------------------------------ */

/**
 * Audiences to add, grouped by the line item they apply to.
 *
 * `audience_expansion_recommendations` is a flat list with one entry per audience, so a line
 * item getting eight audiences arrives as eight rows naming the same line item. Grouping is
 * what makes it readable as "this line item, these audiences".
 *
 * The MMGY prompt asks for first-party and partner ids (MMGY's own Terminal and TRiPs
 * segments plus the LiveRamp partner catalog) rather than Google audiences, so the
 * first-party list leads here — but both are rendered, because the sub-prompt that produces
 * this section is client-agnostic and still asks for a Google audience field.
 *
 * Keyword and PMP-deal expansions are NOT repeated here: they arrive inside
 * `recommendations` and are already rendered as suggestion cards in section 2, with the
 * concrete values in their implementation steps. Duplicating them would make the same change
 * look like two separate asks.
 */
export function MmgyExpansionsSection({ details }) {
  const raw = Array.isArray(details?.audience_expansion_recommendations)
    ? details.audience_expansion_recommendations
    : [];
  if (!raw.length) return null;

  // Preserve first-seen line-item order rather than sorting: the optimizer emits them in the
  // order it worked through the line items.
  const collected = [];
  const byLineItem = new Map();
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const lineItemId = text(entry.line_item_id);
    const key = lineItemId || '—';
    if (!byLineItem.has(key)) {
      const group = { lineItemId, firstParty: [], google: [] };
      byLineItem.set(key, group);
      collected.push(group);
    }
    const group = byLineItem.get(key);
    const firstParty = splitCatalogEntry(entry.first_party_audience_to_expand);
    if (firstParty) group.firstParty.push(firstParty);
    const google = splitCatalogEntry(entry.google_audience_to_expand);
    if (google) group.google.push(google);
  }

  // A group whose every audience value was null or unrenderable would otherwise render an
  // empty card, and its line item would still be counted in the "N audiences across M line
  // items" subtitle — a header promising expansions above a card containing none.
  const groups = collected.filter((g) => g.firstParty.length || g.google.length);
  if (!groups.length) return null;

  const audienceList = (label, items) => {
    if (!items.length) return null;
    return (
      <div style={{ marginTop: '6px' }}>
        <span style={{ fontSize: '0.78em', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 700 }}>
          {label}
        </span>
        <ul style={{ margin: '4px 0 0', paddingLeft: '18px', fontSize: '0.85em', color: '#374151' }}>
          {items.map((a, i) => (
            <li key={`${a.id || a.name}-${i}`} style={{ marginBottom: '2px' }}>
              {a.name}
              {a.id && <> {' '}<IdChip>{a.id}</IdChip></>}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const totalAudiences = groups.reduce((n, g) => n + g.firstParty.length + g.google.length, 0);

  return (
    <Section
      step="3"
      title="Expansions"
      subtitle={`${totalAudiences} audience${totalAudiences === 1 ? '' : 's'} to add across ${groups.length} line item${groups.length === 1 ? '' : 's'}. Add these in DV360 under the line item's audience targeting — quote the id, not the name. A line item that targets contextual keywords has no audience group by construction, so adding one there changes what the line is.`}
    >
      {groups.map((g, i) => (
        <div key={g.lineItemId || i} style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <strong style={{ fontSize: '0.9em' }}>Line item</strong>
            {g.lineItemId ? <IdChip>{g.lineItemId}</IdChip> : <span style={{ fontSize: '0.85em', color: '#6b7280' }}>unspecified</span>}
          </div>
          {audienceList('First-party & partner audiences', g.firstParty)}
          {audienceList('Google audiences', g.google)}
        </div>
      ))}
    </Section>
  );
}
